/**
 * Custom Sections Tab Component
 * Manages custom sections for reports (add, edit, delete, reorder, import)
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../UI';
import { useToastStore } from '../../stores/toast';
import {
  Plus,
  Import,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  getReportSections,
  addCustomSection,
  updateCustomSection,
  deleteCustomSection,
  reorderSections,
  importSections,
  type ReportSection,
  type AddCustomSectionRequest,
  type UpdateCustomSectionRequest,
  type ImportSectionsRequest,
} from '../../api/reports';
import { SectionEditor } from './SectionEditor';
import { AddCustomSectionModal } from './AddCustomSectionModal';
import { SectionImportModal } from './SectionImportModal';
import { DraggableSectionList } from './DraggableSectionList';

interface CustomSectionsTabProps {
  reportId: string;
  report: any;
  onSectionUpdate?: () => void;
}

export function CustomSectionsTab({
  reportId,
  report,
  onSectionUpdate,
}: CustomSectionsTabProps) {
  const { addToast } = useToastStore();
  const queryClient = useQueryClient();
  const [editingSection, setEditingSection] = useState<ReportSection | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingSections, setPendingSections] = useState<ReportSection[]>([]);
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);

  // Fetch sections
  const { data: sectionsData, isLoading, error, refetch } = useQuery({
    queryKey: ['report-sections', reportId],
    queryFn: () => getReportSections(reportId, { includeCustom: true, orderBy: 'display_order' }),
    enabled: !!reportId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const sections = useMemo(() => {
    const serverSections = sectionsData?.sections || [];
    if (!pendingSections.length) return serverSections;

    const serverIds = new Set(serverSections.map((s) => s.id));
    const stillPending = pendingSections.filter((s) => !serverIds.has(s.id));

    // Keep stable ordering by display_order where possible.
    return [...stillPending, ...serverSections].sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
    );
  }, [sectionsData?.sections, pendingSections]);
  const customCount = useMemo(
    () => sections.filter((s) => s.is_custom || s.section_type === 'custom' || s.id.startsWith('temp-')).length,
    [sections]
  );

  // Add section mutation
  const addMutation = useMutation({
    mutationFn: (request: AddCustomSectionRequest) => addCustomSection(reportId, request),
    onMutate: (request: AddCustomSectionRequest) => {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const maxDisplayOrder = sections.reduce((max, s) => Math.max(max, s.display_order ?? -1), -1);
      const nextDisplayOrder = request.position ?? (maxDisplayOrder + 1);
      const optimisticSection: ReportSection = {
        id: tempId,
        section_name: request.section_name,
        question:
          request.content_mode === 'ai' ? request.question || request.section_name : request.section_name,
        answer: request.content_mode === 'manual' ? request.manual_content : undefined,
        confidence: request.content_mode === 'manual' ? 100 : 0,
        citations: [],
        status: request.content_mode === 'ai' ? 'processing' : 'success',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_custom: true,
        section_type: request.section_type || 'custom',
        display_order: nextDisplayOrder,
        content_mode: request.content_mode,
        section_metadata: request.metadata || {},
      };

      setPendingSections((prev: ReportSection[]) => [...prev, optimisticSection]);
      addToast({
        type: 'info',
        message:
          request.content_mode === 'ai'
            ? 'Custom section added. AI generation is processing in the background.'
            : 'Custom section added.',
      });
      return { tempId };
    },
    onSuccess: (createdSection: ReportSection, _request: AddCustomSectionRequest, context?: { tempId?: string }) => {
      if (context?.tempId) {
        setPendingSections((prev: ReportSection[]) => prev.filter((s) => s.id !== context.tempId));
      }

      queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setShowAddModal(false);
      addToast({ message: 'Custom section added successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any, _request: AddCustomSectionRequest, context?: { tempId?: string }) => {
      const isTimeoutOrNetwork =
        error?.code === 'ECONNABORTED' ||
        !error?.response ||
        String(error?.message || '').toLowerCase().includes('timeout');
      if (context?.tempId && !isTimeoutOrNetwork) {
        setPendingSections((prev: ReportSection[]) => prev.filter((s) => s.id !== context.tempId));
      }
      addToast({
        message: isTimeoutOrNetwork
          ? 'Section request is still processing. It will appear once the server responds.'
          : (error.response?.data?.detail || 'Failed to add custom section'),
        type: isTimeoutOrNetwork ? 'info' : 'error',
      });
      if (isTimeoutOrNetwork) {
        queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      }
    },
  });

  // Update section mutation
  const updateMutation = useMutation({
    mutationFn: ({ sectionId, request }: { sectionId: string; request: UpdateCustomSectionRequest }) =>
      updateCustomSection(reportId, sectionId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setEditingSection(null);
      addToast({ message: 'Section updated successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to update section',
        type: 'error',
      });
    },
  });

  // Delete section mutation
  const deleteMutation = useMutation({
    mutationFn: (sectionId: string) => deleteCustomSection(reportId, sectionId),
    onMutate: (sectionId: string) => {
      setDeletingSectionId(sectionId);
    },
    onSuccess: (_data, sectionId) => {
      // Optimistically remove from local sections cache (do not invalidate report-sections here —
      // immediate refetch races the delete and can blank the tab with "Failed to load sections").
      queryClient.setQueryData(['report-sections', reportId], (old: any) => {
        if (!old || !old.sections) return old;
        return {
          ...old,
          sections: old.sections.filter((s: ReportSection) => s.id !== sectionId),
        };
      });
      setPendingSections((prev: ReportSection[]) => prev.filter((s) => s.id !== sectionId));
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      addToast({ message: 'Section deleted successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      addToast({
        message: error.response?.data?.detail || 'Failed to delete section',
        type: 'error',
      });
    },
    onSettled: () => {
      setDeletingSectionId(null);
    },
  });

  // Reorder sections mutation
  const reorderMutation = useMutation({
    mutationFn: (sectionOrders: Array<{ section_id: string; display_order: number }>) =>
      reorderSections(reportId, { section_orders: sectionOrders }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      addToast({ message: 'Sections reordered successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to reorder sections',
        type: 'error',
      });
    },
  });

  // Import sections mutation
  const importMutation = useMutation({
    mutationFn: (request: ImportSectionsRequest) => importSections(reportId, request),
    onSuccess: async () => {
      // Invalidate and refetch to ensure UI updates
      await queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      await queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      // Explicitly refetch to ensure fresh data
      await refetch();
      setShowImportModal(false);
      addToast({ message: 'Sections imported successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to import sections',
        type: 'error',
      });
    },
  });

  const handleEdit = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      setEditingSection(section);
    }
  };

  const handleDelete = (sectionId: string) => {
    // Optimistic temp sections are not persisted on the backend yet.
    if (sectionId.startsWith('temp-')) {
      setPendingSections((prev: ReportSection[]) => prev.filter((s) => s.id !== sectionId));
      return;
    }
    if (confirm('Are you sure you want to delete this section?')) {
      deleteMutation.mutate(sectionId);
    }
  };

  const handleReorder = (newOrder: Array<{ sectionId: string; displayOrder: number }>) => {
    reorderMutation.mutate(
      newOrder.map((o) => ({ section_id: o.sectionId, display_order: o.displayOrder }))
    );
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Card>
    );
  }

  if (error && !sectionsData) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Failed to load sections</p>
        </div>
      </Card>
    );
  }

  const templateResult = report?.meta?.template_result ?? (report as any)?.metadata?.template_result;
  const staffRequirements = templateResult?.staff_requirements as any[] | undefined;
  const hasStaffRequirements = Array.isArray(staffRequirements) && staffRequirements.length > 0;

  return (
    <div className="space-y-4">
      {/* Hint when report has RFP-derived staff requirements */}
      {hasStaffRequirements && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2">
          <span className="text-sm text-blue-900 dark:text-blue-100">
            This proposal has <strong>{staffRequirements.length}</strong> staff position(s) from the RFP.
            Add a <strong>Staff</strong> section type when creating a custom section to write up key personnel.
          </span>
        </div>
      )}
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Report Sections</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {sections.length} total sections ({customCount} custom)
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImportModal(true)}
            disabled={importMutation.isPending}
          >
            <Import className="h-4 w-4 mr-2" />
            Import from Previous
          </Button>
          <Button size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Section
          </Button>
        </div>
      </div>

      {/* Sections List */}
      <DraggableSectionList
        sections={sections}
        reportId={reportId}
        onReorder={handleReorder}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canEdit={true}
        deletingSectionId={deletingSectionId}
      />

      {/* Add Section Modal */}
      {showAddModal && (
        <AddCustomSectionModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          reportId={reportId}
          onSectionCreated={(section) => {
            addMutation.mutate({
              section_name: section.section_name,
              section_type: section.section_type as AddCustomSectionRequest['section_type'] || 'custom',
              content_mode: section.content_mode as 'ai' | 'manual' || 'ai',
              question: section.question,
              manual_content: section.answer,
            });
          }}
        />
      )}

      {/* Edit Section Modal */}
      {editingSection && (
        <SectionEditor
          section={editingSection}
          reportId={reportId}
          mode="edit"
          onSave={async (updatedSection, regenerate = false) => {
            await updateMutation.mutateAsync({
              sectionId: updatedSection.id,
              request: {
                section_name: updatedSection.section_name,
                answer: updatedSection.answer,
                question: updatedSection.question,
                regenerate: regenerate, // Use explicit regenerate flag from SectionEditor
              },
            });
          }}
          onCancel={() => setEditingSection(null)}
        />
      )}

      {/* Import Sections Modal */}
      {showImportModal && (
        <SectionImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          targetReportId={reportId}
          projectId={report?.project_id}
          onImport={(request) => {
            importMutation.mutate(request);
          }}
        />
      )}
    </div>
  );
}
